import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const SeasonDropdown = ({
  options = [],
  selectedId,
  onChange,
  label = "Season",
  id = "season-dropdown",
  isMobile = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const isMobileDevice = useMediaQuery("(max-width: 768px)") || isMobile;

  // Ensure we don't display seasons with empty/missing blocks
  const displayOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : []).filter(
        (s) => Array.isArray(s?.blocks) && s.blocks.length > 0
      ),
    [options]
  );
  
  const selectedOption =
    displayOptions.find((s) => String(s?.season_id) === String(selectedId)) ||
    displayOptions[0] ||
    null;

  const formatLabel = (opt) => {
    if (!opt) return "Select season";
    const league = opt.league_name || "";
    const season = opt.season_name || opt.season_id || "";
    const labelCore = `${league ? `${league} ` : ""}${season}`.trim();
    return `${labelCore}${opt.is_current ? " (Current)" : ""}`;
  };

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  const handleSelect = (seasonId) => {
    onChange?.(seasonId);
    setIsOpen(false);
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  const handleOptionKeyDown = (e, seasonId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(seasonId);
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Prevent body scroll when mobile dropdown is open
  useEffect(() => {
    if (isMobileDevice && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileDevice, isOpen]);

  if (!displayOptions.length) {
    return null;
  }

  return (
    <div className="relative inline-block" ref={containerRef}>
      <div className="flex items-center gap-3">
        <button
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label="Season selector"
          className="border border-[#D6DBEA] rounded-lg px-3 py-2 md:py-4 text-sm text-left text-[#1A212F] focus:outline-none focus:ring-2 focus:ring-[#4282FC] min-w-[180px] flex items-center justify-between"
          onClick={handleToggle}
          onKeyDown={handleButtonKeyDown}
          data-no-translate="true"
        >
          <span 
            key={`season-${selectedOption?.season_id}`}
            className="truncate"
            data-no-translate="true"
          >
            {formatLabel(selectedOption)}
          </span>
          <svg
            className={`ml-2 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M7 10l5 5 5-5"
              stroke="#6B7280"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <>
          {/* Mobile: Full-screen overlay */}
          {isMobileDevice && (
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={handleClose}
            />
          )}
          <div
            role="listbox"
            aria-label="Season options"
            className={`${
              isMobileDevice
                ? "fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-xl shadow-2xl max-h-[70vh] overflow-y-auto"
                : "absolute z-10 mt-2 w-full bg-white border border-[#D6DBEA] rounded-lg shadow-xl max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-[#D6DBEA] scrollbar-track-transparent"
            }`}
          >
            {isMobileDevice && (
              <div className="sticky top-0 bg-white border-b border-[#D6DBEA] px-4 py-3 flex items-center justify-between">
                <span className="text-base font-semibold text-[#1A212F]">
                  Select Season
                </span>
                <button
                  onClick={handleClose}
                  className="text-[#6B7280] hover:text-[#1A212F]"
                  aria-label="Close"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
            <div className={isMobileDevice ? "pb-4" : ""}>
              {displayOptions.map((opt) => {
                const isSelected =
                  String(opt?.season_id) === String(selectedOption?.season_id);
                return (
                  <div
                    key={opt.season_id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={`${
                      isMobileDevice ? "px-4 py-4" : "px-4 py-2.5"
                    } text-sm cursor-pointer transition-colors ${
                      isSelected ? "bg-[#F0F4FF]" : "bg-white"
                    } hover:bg-[#F8FAFF]`}
                    onClick={() => handleSelect(opt.season_id)}
                    onKeyDown={(e) => handleOptionKeyDown(e, opt.season_id)}
                    data-no-translate="true"
                  >
                    <span data-no-translate="true">{formatLabel(opt)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SeasonDropdown;
